/**
 * fetch + ReadableStream 기반 SSE 유틸리티
 * EventSource는 GET + 헤더 추가 불가 → fetch 방식 사용
 * 401 자동 처리 포함
 */
import { tokenStorage } from '../lib/auth'
import { getCsrfTokenFromCookie } from '../lib/csrf'

export interface SSECallbacks {
  onEvent: (event: string, data: string) => void
  onError?: (err: Error) => void
  onDone?: () => void
}

// SSE 엔드포인트는 /chat, /analyze, /compare 등 "api 접두어가 없는" 경로.
// same-origin 으로 호출하면 Vite preview/Nginx 프록시가 백엔드(8501)로 그대로 전달.
// 명시적 BASE 지정이 필요하면 VITE_SSE_BASE_URL 로 오버라이드.
const BASE_URL = import.meta.env['VITE_SSE_BASE_URL'] ?? ''

export interface SSERequestOptions {
  /** POST 사용 시 JSON body. 지정하면 자동으로 method=POST + Content-Type: application/json. */
  body?: Record<string, unknown>
  /** multipart/form-data POST. body 와 동시 지정 시 formData 우선. */
  formData?: FormData
  /** 명시적 method override (기본: body/formData 있으면 POST, 없으면 GET). */
  method?: 'GET' | 'POST'
}

export async function openSSEStream(
  url: string,
  callbacks: SSECallbacks,
  signal?: AbortSignal,
  options: SSERequestOptions = {},
) {
  const token = tokenStorage.getAccess()
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const method = options.method ?? (options.body || options.formData ? 'POST' : 'GET')
  // 뮤테이션 메서드 + /api/* 경로면 CSRF 헤더 필수 (CsrfMiddleware)
  if (method !== 'GET' && url.startsWith('/api/')) {
    const csrf = getCsrfTokenFromCookie()
    if (csrf) headers['X-CSRF-Token'] = csrf
  }
  const fetchInit: RequestInit = { method, headers, signal }
  if (options.formData) {
    // Content-Type 은 브라우저가 boundary 포함해 자동 설정. 직접 지정 금지.
    fetchInit.body = options.formData
  } else if (options.body) {
    headers['Content-Type'] = 'application/json'
    fetchInit.body = JSON.stringify(options.body)
  }

  let res: Response
  try {
    res = await fetch(`${BASE_URL}${url}`, fetchInit)
  } catch (e) {
    callbacks.onError?.(e instanceof Error ? e : new Error(String(e)))
    return
  }

  if (res.status === 401) {
    window.location.href = '/login'
    return
  }

  if (!res.ok || !res.body) {
    callbacks.onError?.(new Error(`HTTP ${res.status}`))
    return
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE 메시지는 빈 줄로 구분
      const parts = buffer.split('\n\n')
      buffer = parts.pop() ?? ''

      for (const part of parts) {
        let eventName = 'message'
        let dataLines: string[] = []

        for (const line of part.split('\n')) {
          if (line.startsWith('event:')) {
            eventName = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trim())
          }
        }

        if (dataLines.length > 0) {
          callbacks.onEvent(eventName, dataLines.join('\n'))
        }
      }
    }
  } catch (e) {
    if ((e as Error).name !== 'AbortError') {
      callbacks.onError?.(e instanceof Error ? e : new Error(String(e)))
    }
  } finally {
    callbacks.onDone?.()
  }
}
